import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";

import {
  CreateApiKeyMutation,
  CreateApiKeyMutationVariables,
} from "~frontend/__generated__/graphql";
import ReadOnlyClickToCopyList from "~frontend/components/ReadOnlyClickToCopyList";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfErrorBanner,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { CREATE_API_KEY, LIST_API_KEYS } from "~frontend/graphql/apiKeys";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import RhfApiKeyScopesField, {
  scopesToValue,
  valueToScopes,
} from "./RhfApiKeyScopesFields";
import FormSchema from "./formSchema";

type FormValues = {
  name: string;
  scopes: { [name: string]: boolean };
};

export function CreateApiKeyDialog({
  open,
  onClose,
  scopes,
}: {
  open: boolean;
  onClose: () => void;
  scopes: string[];
}): ReactElement {
  const { control, handleSubmit, formState, setError, reset } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      scopes: scopesToValue({ allScopes: scopes, selectedScopes: [] }),
    },
  });

  const [result, setResult] = useState<
    null | CreateApiKeyMutation["createApiKey"]
  >(null);

  const [createApiKey] = useMutation<
    CreateApiKeyMutation,
    CreateApiKeyMutationVariables
  >(CREATE_API_KEY, {
    refetchQueries: [LIST_API_KEYS],
  });

  const formatErrorMessage = useErrorMessageFormatter();

  const handleCreateApiKey = async (values: FormValues) => {
    const { errors, data } = await createApiKey({
      variables: {
        input: {
          name: values.name,
          scopes: valueToScopes({
            allScopes: scopes,
            formValue: values.scopes,
          }),
        },
      },
    });

    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
    } else {
      reset();
      setResult(data!.createApiKey);
    }
  };

  function handleClose() {
    setResult(null);
    onClose();
  }

  if (result) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>API Key Name: </strong>
            {result.name}
          </Typography>
          <Typography sx={{ my: 1 }}>
            <strong>API Key Scopes: </strong>
            {result.scopes.length > 0 ? result.scopes.join(", ") : "No scopes"}
          </Typography>
          <Alert severity="warning">
            Keep your API Key Secret in a safe place. You won't be able to view
            it again.
          </Alert>
          <ReadOnlyClickToCopyList
            listData={[
              {
                label: "Base URL",
                value: `${import.meta.env.STW_API_URL}/api/v1/`,
              },
              { label: "API Key ID (Basic Auth Username)", value: result.id },
              {
                label: "API Key Secret (Basic Auth Password)",
                value: result.secret,
              },
            ]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create API Key</DialogTitle>
      <form onSubmit={handleSubmit(handleCreateApiKey)}>
        <DialogContent>
          <RhfErrorBanner control={control} />
          <RhfTextField control={control} name="name" label="Name" fullWidth />

          <RhfApiKeyScopesField
            control={control}
            namePrefix="scopes"
            label="Scopes"
            scopes={scopes}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={formState.isSubmitting}
            onClick={handleClose}
            color="secondary"
          >
            Cancel
          </Button>

          <SubmitButton
            isSubmitting={formState.isSubmitting}
            color="primary"
            variant="contained"
            submitText="Create"
            loadingText="Creating..."
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
